<script setup>
/**
 * A Melbourne suburban train, side-on, and the two things that happen to a
 * suburban line: a level crossing, and the crossing being taken away.
 *
 * The world moves and the train stays put — it is the view from the window.
 * Every four seconds the pattern repeats: a boom-gate crossing, drawn in two
 * layers like the one at Brighton Beach with the far post and signal hut
 * behind the train and the near post, boom, picket fence and sign in front of
 * it; then the ground opens into a linear park, the train climbs the ramp onto
 * the viaduct and runs through a sky rail station, the way Carnegie and
 * Murrumbeena were rebuilt; then open track again.
 *
 * The rise is deliberately SLIGHT — four units, the ramp's own slope at the
 * sleepers' speed. The train is 164 units long in a 220-unit window, so no
 * honest ramp can be under all of it at once; what sells the height is the
 * ground falling away underneath into the park, the columns, and the platform
 * screen passing in front. A bigger rise only made the gap under the rear of
 * the train bigger.
 *
 * THE TIMING IS FIXED; THE TRAIN AND THE WORLD ARE NOT. The four-second loop,
 * the 800-unit pattern, the sleepers' speed, the train's place in the window
 * and every percentage in the keyframes belong to this file and never change.
 * What runs through it is whatever `livery` names, drawn from the table in
 * `src/liveries.js`: an HCMT in Melbourne, a Waratah in Sydney, a VLocity on a
 * regional line, an Urbos in Canberra. What it runs THROUGH is whatever
 * `backdrop` names, drawn from the table in `src/backdrops.js`: the crossing
 * and the sky rail above, the Harbour Bridge in Sydney, the Merivale bridge in
 * Brisbane, the Oval across the Torrens in Adelaide, Northbourne Avenue in
 * Canberra. Both default to Melbourne's, and Melbourne's is the same scene, to
 * the character, that this file used to draw by hand.
 *
 * MELBOURNE'S WORLD IS STILL DRAWN HERE, in the markup below, rather than as a
 * list of paths in the table. That is deliberate and it is tested: it is the
 * scene every person who has ever opened this app has been looking at, and a
 * generic renderer would redraw the same picture with different elements in a
 * different order. Every other city's world is data. See the note at the top of
 * `src/backdrops.js`.
 *
 * Under prefers-reduced-motion everything holds still.
 */
import { computed } from 'vue';
import { DEFAULT_LIVERY, livery as liveryOf, shellPath } from '../liveries.js';
import { DEFAULT_BACKDROP, backdrop as backdropOf } from '../backdrops.js';

/**
 * `phase` — where in the loop the scene starts, as a fraction of it.
 *
 * The loop tells a story — crossing, climb, station, descent — and a loader
 * on screen for two seconds only ever sees part of it. So the screens that
 * wait longest start at the crossing, and the splash starts on the approach
 * to the viaduct, which puts the climb and the station inside the time it is
 * up. A fraction rather than seconds, so changing the speed of the world does
 * not silently retime every caller.
 *
 * `livery` — which train. An id from `src/liveries.js`; anything unknown falls
 * back to Melbourne's rather than drawing nothing, because a loader with no
 * train on it is a loader that looks broken.
 *
 * `backdrop` — which world. A city id from `src/backdrops.js`, or a livery id,
 * which resolves to the city that train belongs to. Same fallback, same
 * reason: a loader with no scenery is a train hanging in the dark.
 */
const props = defineProps({
  phase: { type: Number, default: 0 },
  livery: { type: String, default: DEFAULT_LIVERY },
  backdrop: { type: String, default: DEFAULT_BACKDROP },
});

/** The paint, and the outline everything is drawn inside. */
const paint = computed(() => liveryOf(props.livery));

/** The world, and whether it is the one this file draws by hand. */
const world = computed(() => backdropOf(props.backdrop));
const melbourne = computed(() => world.value.id === 'melbourne');

/**
 * The scene's two custom properties, and why only one of them is always there.
 *
 * `--phase` has been on this element since the scene was written. `--lift` is
 * the world's business — a viaduct is climbed, a river bridge is risen over, a
 * harbour bridge is level — and it is set ONLY when the world is not
 * Melbourne's, so that Melbourne's `style` attribute comes out character for
 * character as it always has. The stylesheet's `var(--lift, climb)` reads the
 * absence as "climb", which is exactly what Melbourne does.
 */
const sceneStyle = computed(() => {
  const style = { '--phase': props.phase };
  if (world.value.lift !== 'climb') style['--lift'] = world.value.lift;
  return style;
});
const shell = computed(() => paint.value.shell);
const outline = computed(() => shellPath(paint.value.shell));

/**
 * The rectangle the body is painted on.
 *
 * Deliberately larger than the outline on every side and then clipped to it,
 * which is what the hand-written version did: a fill that stops exactly on a
 * curve leaves a hairline of background along it at some zoom levels, and this
 * scene is drawn at everything from 220 to 390 device pixels wide.
 */
const bodyBox = computed(() => {
  const s = paint.value.shell;
  return { x: s.x0 - 4, y: s.top - 2, w: (s.x1 - s.x0) + 12, h: (s.bottom - s.top) + 4 };
});

/** Doors run from just under the roof to the floor; their glass sits up top. */
const doorBox = computed(() => {
  const s = paint.value.shell;
  return {
    y: s.top + 2,
    h: s.bottom - s.top - 2,
    glassY: s.top + 4,
    glassH: paint.value.door.paneH ?? (paint.value.tram ? 6.4 : 8),
  };
});

/**
 * Where one carriage ends and the next begins.
 *
 * Evenly spaced, because a set of identical carriages is evenly spaced. A
 * livery with `seam: null` — Melbourne's — gets none: the HCMT's drawn length
 * is less than one of its cars, so a joint anywhere in it would be a joint
 * that is not there.
 *
 * A tram's joint is a full-height bellows, which is most of what a tram looks
 * like from the side; a train's is a hairline at the roof and a wider one
 * through the skirt, which is all you see of a coupling from the street.
 */
const seams = computed(() => {
  const p = paint.value;
  if (!p.seam) return [];
  const s = p.shell;
  if (p.joins) return p.joins;
  const step = (s.x1 - s.x0) / p.carriages;
  return Array.from({ length: p.carriages - 1 }, (_, i) => s.x0 + step * (i + 1));
});

/** A door's width and its glass panes: a 12 wide pair of 3.4 panes unless the livery says otherwise. */
const doorW = computed(() => paint.value.door.w ?? 12);
const pane = computed(() => paint.value.door.pane ?? 3.4);
const paneX = computed(() => (paint.value.door.w ? 0.8 : 2));

/** The pantograph, folded, sitting on the roof. Electric liveries only. */
const pan = computed(() => {
  const t = paint.value.shell.top;
  const x = paint.value.panX ?? 76;
  // A tram's is a single angled arm, a Z, with its head bar over the knee.
  if (paint.value.symmetric) return { arm: `M${x - 3} ${t - 1} L${x + 3} ${t - 5.5} L${x - 2} ${t - 10}`, x: x - 7, w: 10, base: { x: x - 6, y: t - 1.4, w: 10 } };
  return { arm: `M${x - 10} ${t} L${x} ${t - 9} L${x + 10} ${t}`, x: x - 8, w: 18, base: null };
});

/** The marker light on the nose, and the wheels. */
const lampX = computed(() => paint.value.shell.x1 - 4);

// The clip that gives the livery the body's outline, unique per instance so
// two scenes on one page (the splash over a loading tab) do not share one.
let instances = 0;
const clipId = `la-train-${++instances}`;
const shellId = `la-shell-${instances}`;
const finId = `la-fins-${instances}`;

// Two copies of the pattern, one spacing apart, so the scene wraps without a
// seam: the group slides one spacing per cycle and the second copy takes the
// first one's place. Speed is the sleepers' speed — 80 units a second — and
// the pattern holds one crossing and one elevated section, so the crossing is
// under the train half a second in and the station three and a half seconds
// later, with open track before the next crossing.
const SPACING = 800;
const COPIES = [0, SPACING];
</script>

<template>
  <svg class="scene" viewBox="0 -24 220 70" aria-hidden="true" :style="sceneStyle">
    <title>{{ paint.name }}</title>
    <!-- WASH: behind everything and NOT scrolling — water, haze. Water has no
         seam, so it may not be drawn in a pattern that wraps. Melbourne's is
         empty and this element is not rendered at all. -->
    <g v-if="world.wash.length" class="wash world">
      <template v-for="(s, i) in world.wash" :key="`wash${i}`">
        <circle v-if="s.r" :cx="s.cx" :cy="s.cy" :r="s.r" :fill="s.fill" :opacity="s.opacity" />
        <path v-else :d="s.d" :fill="s.fill ?? 'none'" :stroke="s.stroke" :stroke-width="s.sw" :opacity="s.opacity" />
      </template>
    </g>

    <!-- SKY and MID: the slow layers of a street that has them (the boulevard):
         a distant skyline at a quarter of the ground's speed and a row of
         median trees at half. A world without them renders neither. -->
    <g v-if="world.sky?.length" class="scenery scenery-sky">
      <g v-for="dx in COPIES" :key="`sky${dx}`" class="world" :transform="`translate(${dx + 14})`">
        <template v-for="(s, i) in world.sky" :key="`sky${i}`">
          <circle v-if="s.r" :cx="s.cx" :cy="s.cy" :r="s.r" :fill="s.fill" :opacity="s.opacity" />
          <path v-else :d="s.d" :fill="s.fill ?? 'none'" :stroke="s.stroke" :stroke-width="s.sw" :opacity="s.opacity" />
        </template>
      </g>
    </g>
    <g v-if="world.mid?.length" class="scenery scenery-mid">
      <g v-for="dx in COPIES" :key="`mid${dx}`" class="world" :transform="`translate(${dx + 14})`">
        <template v-for="(s, i) in world.mid" :key="`mid${i}`">
          <circle v-if="s.r" :cx="s.cx" :cy="s.cy" :r="s.r" :fill="s.fill" :opacity="s.opacity" />
          <path v-else :d="s.d" :fill="s.fill ?? 'none'" :stroke="s.stroke" :stroke-width="s.sw" :opacity="s.opacity" />
        </template>
      </g>
    </g>

    <!-- FAR SIDE: behind the train. -->
    <g v-if="melbourne" class="scenery">
      <g v-for="dx in COPIES" :key="`far${dx}`" :transform="`translate(${dx + 14})`">
        <!-- Signal hut, tan with a red roof, past the far post. -->
        <polygon class="roof" points="148,13 160,3 172,13" />
        <rect class="chimney" x="166" y="4" width="2.5" height="6" />
        <rect class="hut" x="150" y="12" width="20" height="26" />
        <rect class="door" x="157" y="24" width="5" height="14" />
        <!-- Far post: crossbuck and lights. -->
        <rect class="post" x="139.2" y="-8" width="1.6" height="46" />
        <g class="crossbuck">
          <rect x="133" y="-8.9" width="14" height="1.8" rx="0.4" transform="rotate(35 140 -8)" />
          <rect x="133" y="-8.9" width="14" height="1.8" rx="0.4" transform="rotate(-35 140 -8)" />
        </g>
        <rect class="post" x="135" y="-3" width="10" height="1.2" />
        <circle class="light light-a" cx="136.8" cy="-0.2" r="1.7" />
        <circle class="light light-b" cx="143.2" cy="-0.2" r="1.7" />

        <!-- The station's inside, which is all that is BEHIND the train:
             the dim far platform the lit carriages are seen against. The
             building itself stands in front — see the near layer. -->
        <rect class="platform-wall" x="452" y="0" width="124" height="34" />
      </g>
    </g>
    <g v-else class="scenery">
      <g v-for="dx in COPIES" :key="`far${dx}`" class="world" :transform="`translate(${dx + 14})`">
        <template v-for="(s, i) in world.far" :key="`far${i}`">
          <circle v-if="s.r" :cx="s.cx" :cy="s.cy" :r="s.r" :fill="s.fill" :opacity="s.opacity" />
          <path v-else :d="s.d" :fill="s.fill ?? 'none'" :stroke="s.stroke" :stroke-width="s.sw" :opacity="s.opacity" />
        </template>
      </g>
    </g>

    <!-- Track: rail, then sleepers that slide under the train. -->
    <rect class="rail" x="0" y="38" width="220" height="2" />
    <g class="sleepers">
      <rect v-for="i in 16" :key="i" :x="i * 18 - 36" y="40" width="8" height="3" rx="1" />
    </g>

    <!-- GROUND: in front of the sleepers, behind the train. The park that
         replaced the track, and the structure the train runs on. -->
    <g v-if="melbourne" class="scenery">
      <g v-for="dx in COPIES" :key="`deck${dx}`" :transform="`translate(${dx + 14})`">
        <!-- Where the rails used to be: grass, a shared path, a few trees. -->
        <rect class="park" x="248" y="38" width="524" height="10" />
        <rect class="path" x="248" y="44" width="524" height="1.4" />
        <g v-for="i in 8" :key="`tree${i}`" class="tree">
          <rect class="trunk" :x="268 + i * 62" y="40.5" width="1.2" height="4" />
          <circle class="leaves" :cx="268.6 + i * 62" cy="39.6" r="3.1" />
        </g>

        <!-- The viaduct. The ramps are 160 units long — as long as the train —
             so the wheels have structure under them the whole way up and the
             whole way down, and the grade is the one and a half degrees the
             train actually leans at. A short ramp made the climb a lurch. -->
        <polygon class="deck-under" points="260,38 420,34 420,38 260,42" />
        <polygon class="deck-under" points="598,34 758,38 758,42 598,38" />
        <rect class="deck" x="418" y="34" width="182" height="4" />
        <!-- Columns: three under the deck, and stubs under each ramp, each one
             meeting the underside where it happens to be. -->
        <g v-for="i in 3" :key="`col${i}`">
          <polygon class="column" :points="`${434 + i * 60},38 ${446 + i * 60},38 ${443 + i * 60},47 ${437 + i * 60},47`" />
        </g>
        <polygon class="column" points="325,40.4 335,40.4 332.5,47 327.5,47" />
        <polygon class="column" points="375,39.1 385,39.1 382.5,47 377.5,47" />
        <polygon class="column" points="655,39.4 665,39.4 662.5,47 657.5,47" />
        <polygon class="column" points="705,40.7 715,40.7 712.5,47 707.5,47" />
      </g>
    </g>
    <g v-else class="scenery">
      <g v-for="dx in COPIES" :key="`deck${dx}`" class="world" :transform="`translate(${dx + 14})`">
        <template v-for="(s, i) in world.ground" :key="`gnd${i}`">
          <circle v-if="s.r" :cx="s.cx" :cy="s.cy" :r="s.r" :fill="s.fill" :opacity="s.opacity" />
          <path v-else :d="s.d" :fill="s.fill ?? 'none'" :stroke="s.stroke" :stroke-width="s.sw" :opacity="s.opacity" />
        </template>
      </g>
    </g>

    <!-- The train, whichever one it is. Everything below is `paint` — the
         livery table's entry for this id — and nothing below is a colour
         chosen here. The lift is the climb onto the viaduct; the sway inside
         it is the ride. -->
    <g class="lift">
      <g class="train">
        <defs>
          <clipPath :id="clipId">
            <path :d="outline" />
          </clipPath>
        </defs>

        <!-- The pantograph stands ON the roof, so it is outside the clip.
             Absent on the VLocity, which is a diesel, and on the HCMT, whose
             pantograph is on a car past the end of the drawn set. -->
        <g v-if="paint.pantograph" class="pantograph">
          <path :d="pan.arm" />
          <rect v-if="pan.base" :x="pan.base.x" :y="pan.base.y" :width="pan.base.w" height="1.6" rx="0.5" />
          <rect :x="pan.x" :y="shell.top - (pan.base ? 10.4 : 10.6)" :width="pan.w" height="1.2" rx="0.4" />
        </g>

        <g :clip-path="`url(#${clipId})`">
          <rect class="body" :x="bodyBox.x" :y="bodyBox.y" :width="bodyBox.w" :height="bodyBox.h" :fill="paint.body" />
          <!-- A roof of its own, where the base coat does not run over it. -->
          <rect v-if="paint.roof" :x="bodyBox.x" :y="shell.top" :width="bodyBox.w" height="2.4" :fill="paint.roof" />
          <!-- The livery: bands, sweeps and facets, in the table's order. -->
          <path v-for="(mark, i) in paint.side" :key="`mark${i}`" :d="mark.d" :fill="mark.fill" />
          <rect class="skirt" :x="bodyBox.x" :y="shell.bottom - 2" :width="bodyBox.w" height="4" :fill="paint.skirt" />
          <!-- The nose, wrapping or raked or flat, as the front end is. -->
          <path class="nose" :d="paint.nose.d" :fill="paint.nose.fill" />
          <!-- Doors, with a split down the middle. -->
          <g v-for="x in paint.doors" :key="`door${x}`" class="train-door" :transform="`translate(${x} 0)`">
            <rect
              class="door-panel" x="0" :y="doorBox.y" :width="doorW" :height="doorBox.h"
              :fill="paint.door.panel" :stroke="paint.door.trim"
            />
            <rect :x="paneX" :y="doorBox.glassY" :width="pane" :height="doorBox.glassH" rx="0.8" :fill="paint.door.glass" class="door-glass" />
            <rect :x="doorW - paneX - pane" :y="doorBox.glassY" :width="pane" :height="doorBox.glassH" rx="0.8" :fill="paint.door.glass" class="door-glass" />
            <rect class="door-split" :x="doorW / 2 - 0.4" :y="doorBox.y" width="0.8" :height="doorBox.h" :fill="paint.door.trim" />
          </g>
          <!-- Windows. -->
          <rect
            v-for="(w, i) in paint.windows"
            :key="`win${i}`"
            class="glass"
            :x="w.x" :y="w.y" :width="w.w" :height="w.h" rx="1.2"
            :fill="paint.door.glass"
          />
          <!-- Where one carriage meets the next. -->
          <template v-for="(x, i) in seams" :key="`seam${i}`">
            <rect v-if="paint.tram" :x="x - 1.6" :y="shell.top" width="3.2" :height="shell.bottom - shell.top" :fill="paint.seam" />
            <template v-else>
              <rect :x="x - 0.6" :y="shell.top" width="1.2" height="4" :fill="paint.seam" />
              <rect :x="x - 0.8" :y="shell.bottom - 7" width="1.6" height="7" :fill="paint.seam" />
            </template>
          </template>
          <!-- The cab. Null on the metro, which has no driver and no window. -->
          <path
            v-if="paint.cabGlass"
            class="cab-glass"
            :d="paint.cabGlass.d" :fill="paint.cabGlass.fill" :stroke="paint.cabGlass.stroke"
          />
        </g>
        <circle class="lamp" :cx="lampX" :cy="paint.symmetric ? 32.8 : 30" :r="paint.symmetric ? 1.3 : 2" />
        <!-- A tram has a cab at both ends: the same lamp on the other one. -->
        <circle v-if="paint.symmetric" class="lamp" :cx="shell.x0 + 4" cy="32.8" r="1.3" />
        <rect v-for="(b, i) in paint.bogies" :key="`bogie${i}`" class="bogie" :x="b.x" y="35" :width="b.w" height="4" rx="2" />
        <!-- A road vehicle's round tyres: tyre, pale rim, dark hub. -->
        <g v-for="(w, i) in paint.wheels" :key="`wheel${i}`" class="wheel">
          <circle :cx="w.x" :cy="w.y" :r="w.r" :fill="w.tyre" stroke="#5a626b" stroke-width="0.45" />
          <circle :cx="w.x" :cy="w.y" :r="w.r * 0.62" :fill="w.rim" />
          <circle :cx="w.x" :cy="w.y" :r="w.r * 0.28" :fill="w.hub" />
        </g>
      </g>
    </g>

    <!-- NEAR SIDE: in front of the train. -->
    <g v-if="melbourne" class="scenery">
      <g v-for="dx in COPIES" :key="`near${dx}`" :transform="`translate(${dx + 14})`">
        <!-- The road across the rails, with the rail running through it. -->
        <rect class="road" x="114" y="33" width="24" height="13" />
        <rect class="road-line" x="125.4" y="34" width="1.2" height="3" />
        <rect class="road-line" x="125.4" y="41" width="1.2" height="3" />
        <rect class="rail" x="114" y="38" width="24" height="2" />
        <!-- Picket fence either side, and the RAILWAY CROSSING sign on it. -->
        <g class="fence">
          <rect v-for="i in 9" :key="`l${i}`" :x="78 + i * 3.2" y="28" width="1.6" height="10" rx="0.6" />
          <rect x="79" y="30" width="28" height="0.9" />
          <rect x="79" y="35" width="28" height="0.9" />
          <rect v-for="i in 9" :key="`r${i}`" :x="142 + i * 3.2" y="28" width="1.6" height="10" rx="0.6" />
          <rect x="143" y="30" width="28" height="0.9" />
          <rect x="143" y="35" width="28" height="0.9" />
        </g>
        <rect class="sign" x="84" y="25.5" width="18" height="7.5" rx="0.6" />
        <rect class="sign-text" x="86" y="27.3" width="14" height="1.5" />
        <rect class="sign-text" x="86" y="30.2" width="12" height="1.5" />
        <!-- Near post: crossbuck, lights, STOP plate, and the boom down. -->
        <rect class="post" x="109" y="-6" width="2" height="52" />
        <g class="crossbuck">
          <rect x="102" y="-6.9" width="16" height="2" rx="0.4" transform="rotate(35 110 -6)" />
          <rect x="102" y="-6.9" width="16" height="2" rx="0.4" transform="rotate(-35 110 -6)" />
        </g>
        <rect class="post" x="104.5" y="0.5" width="11" height="1.4" />
        <circle class="light light-a" cx="106.3" cy="3.8" r="2.1" />
        <circle class="light light-b" cx="113.7" cy="3.8" r="2.1" />
        <rect class="sign" x="107" y="8" width="6" height="4" rx="0.4" />
        <rect class="sign-text" x="108" y="9" width="4" height="0.8" />
        <rect class="sign-text" x="108" y="10.5" width="3" height="0.8" />
        <rect class="counterweight" x="103" y="27.6" width="6" height="3.2" rx="0.6" />
        <g class="boom">
          <rect v-for="i in 6" :key="`b${i}`" :class="i % 2 ? 'stripe-red' : 'stripe-white'" :x="111 + (i - 1) * 5" y="28.2" width="5" height="2.2" />
        </g>
        <circle class="pivot" cx="110" cy="29.3" r="1.5" />

        <!-- The station, in front of the train exactly as it is from the
             street. No edge screen along the deck: at this size its two ends
             either side of the shell read as stray grey bars, not a parapet. -->
        <!-- THE STATION, standing in front of the train the way Noble Park
             and Carnegie do from the street: a ribbed metal shell sweeping
             over the roof and down the near side as a wall, the slot between
             them that the lit carriages show through, and the long slanted
             louvred opening cut into the wall. -->
        <g class="station">
          <defs>
            <clipPath :id="shellId">
              <path d="M434 34 L455 -3 Q456.5 -6 460 -6 L594 -6 Q597.5 -6 596 -3 L575 34 Z" />
            </clipPath>
            <clipPath :id="finId">
              <rect x="470" y="4" width="98" height="12" />
            </clipPath>
          </defs>

          <!-- The shell: a long raked box standing ON the deck — parallel top
               and bottom, both ends cut at the same forward lean, the way it
               stands over Centre Road. Tall enough to hide the carriages
               entirely as they pass behind it, which is what the street sees.
               No arch over the top: the building has a back, and this is its
               front. -->
          <path class="shell" d="M434 34 L455 -3 Q456.5 -6 460 -6 L594 -6 Q597.5 -6 596 -3 L575 34 Z" />

          <g :clip-path="`url(#${shellId})`">
            <!-- Vertical ribbing over the whole face. -->
            <rect v-for="i in 60" :key="`srib${i}`" class="shell-rib" :x="428 + i * 3" y="-8" width="1" height="46" />
            <!-- The band of angled fins, across the middle of the face and
                 stopping short of both ends, which is where it sits on the
                 real thing. Clipped to its own band so the ribbing above and
                 below stays ribbing. -->
            <g :clip-path="`url(#${finId})`">
              <rect class="fin-band" x="470" y="4" width="98" height="12" />
              <rect
                v-for="i in 28"
                :key="`fin${i}`"
                class="fin"
                :x="468 + i * 3.5"
                y="1"
                width="1.4"
                height="18"
                :transform="`rotate(18 ${468.7 + i * 3.5} 10)`"
              />
            </g>
            <rect class="fin-shadow" x="470" y="15.4" width="98" height="0.8" />
            <rect class="fin-shadow" x="470" y="3.8" width="98" height="0.8" />
          </g>

          <path class="roof-lip" d="M455 -3 Q456.5 -6 460 -6 L594 -6 Q597.5 -6 596 -3" />

          <!-- Warm light where the shell meets the platform. -->
          <rect class="glow" x="448" y="32.6" width="112" height="1.1" />
        </g>

      </g>
    </g>
    <g v-else class="scenery">
      <g v-for="dx in COPIES" :key="`near${dx}`" class="world" :transform="`translate(${dx + 14})`">
        <template v-for="(s, i) in world.near" :key="`near${i}`">
          <circle v-if="s.r" :cx="s.cx" :cy="s.cy" :r="s.r" :fill="s.fill" :opacity="s.opacity" />
          <path v-else :d="s.d" :fill="s.fill ?? 'none'" :stroke="s.stroke" :stroke-width="s.sw" :opacity="s.opacity" />
        </template>
      </g>
    </g>
  </svg>
</template>

<style scoped>
.scene { display: block; width: 220px; max-width: 100%; height: auto; aspect-ratio: 220 / 70; overflow: hidden; }

.rail { fill: var(--la-rule); }
.sleepers rect { fill: var(--la-rule); }

/* One clock for the whole scene. The pattern is 800 units, so the world runs
   at 200 units a second, and the sleepers — 18 units apart — step in 0.0225
   of the loop. Everything below is expressed against `--loop` so the speed is
   one number to change. `climb` is on a wrapper because the sway inside it is a
   transform too, and two animations on one element do not compose. */
.scene { --loop: 4s; }
.sleepers { animation: sleepers calc(var(--loop) * 0.0225) linear infinite; }
.scenery { animation: scenery var(--loop) linear infinite; }
/* PARALLAX: the same 800-unit pattern, drawn twice, so a slower layer wraps as
   cleanly as the ground does. The skyline takes four loops to cross, the
   median trees two; the ground and the footpath's trees take one. */
.scenery-sky { animation-duration: calc(var(--loop) * 4); }
.scenery-mid { animation-duration: calc(var(--loop) * 2); }
/* WHICH lift is the world's business, not the scene's: a viaduct is climbed, a
   river bridge is risen over, a harbour bridge is level. The fallback is
   `climb`, so a world that sets nothing — Melbourne's, which is the only one
   that sets nothing — behaves exactly as this line always did. */
.lift { animation: var(--lift, climb) var(--loop) linear infinite; transform-origin: 100px 24px; }
/* All three read the same clock, so they take the same offset. The sleepers
   do not: their cycle is a quarter of a second and any phase of it is the
   same picture. */
.scenery, .lift { animation-delay: calc(var(--loop) * var(--phase, 0) * -1); }
.scenery-sky { animation-delay: calc(var(--loop) * 4 * var(--phase, 0) * -1); }
.scenery-mid { animation-delay: calc(var(--loop) * 2 * var(--phase, 0) * -1); }

/* The boulevard's colours, as tokens: muted greens and blue-greys that sit
   behind the vehicle on a near-black page and on warm paper alike. */
.scene {
  --st-leaf-a: #2f4f38;
  --st-leaf-b: #43704c;
  --st-trunk: #5d5348;
  --st-sky: #5d6977;
  --st-landmark: #9aa8b8;
  --st-grass: #35553c;
  --st-path: #565d65;
  --st-road: #3b4149;
  --st-kerb: #6b737d;
  --st-pole: #7d858d;
}
:root[data-scheme='light'] .scene {
  --st-leaf-a: #6f9777;
  --st-leaf-b: #8db391;
  --st-trunk: #8c8174;
  --st-sky: #a7b1bd;
  --st-landmark: #6f7d8d;
  --st-grass: #7da283;
  --st-path: #b9b6ae;
  --st-road: #6a7078;
  --st-kerb: #98a0a8;
  --st-pole: #8a9199;
}

/* THE PAINT IS NOT HERE ANY MORE. Every fill on the train comes from the
   livery table in `src/liveries.js` and is bound as an attribute, because the
   colours are now a property of which train this is rather than of this
   stylesheet. What is left below is the part that is true of every train
   whatever it is wearing: how dark glass is, how thick a door surround is,
   what the wheels are made of. Fixed rather than themed, on purpose: the train
   is the one thing in the app that is not the interface. */
.door-panel { stroke-width: 0.9; }
.door-glass { opacity: 0.9; }
.door-split { opacity: 0.8; }
.glass { opacity: 0.9; }
.cab-glass { stroke-width: 1; }
.pantograph path { fill: none; stroke: #6e767f; stroke-width: 1.2; stroke-linejoin: round; }
.pantograph rect { fill: #6e767f; }
.bogie { fill: #3a424c; }
.lamp { fill: #fff3c4; animation: lamp 1.6s ease-in-out infinite; }
.train { animation: sway 1.8s ease-in-out infinite; transform-origin: 100px 30px; }

.post, .counterweight, .pivot { fill: #9aa1aa; }
.crossbuck rect { fill: var(--la-enamel); }
.light { fill: #ff4a3d; opacity: 0.22; }
.light-a { animation: flash 1s steps(1) infinite; }
.light-b { animation: flash 1s steps(1) infinite; animation-delay: 0.5s; }
.sign { fill: var(--la-enamel); }
.sign-text { fill: var(--la-carriage); opacity: 0.85; }
.fence rect { fill: var(--la-enamel); }
.stripe-red { fill: var(--la-signal); }
.stripe-white { fill: var(--la-enamel); }
.road { fill: #3b4149; }
.road-line { fill: var(--la-enamel); opacity: 0.6; }
.hut { fill: #b99a66; }
.roof { fill: var(--la-signal); }
.chimney { fill: #6d6259; }
.door { fill: var(--la-carriage); opacity: 0.8; }

/* Sky rail: concrete in two tones, the station's ribbed silver shell, and the park the
   track used to be. Nothing here is saturated — the train stays the only
   thing in the scene with a colour of its own. */
.deck { fill: #79828d; }
.deck-under { fill: #5e666f; }
.column { fill: #6b737d; }
.shell { fill: #9aa2ab; }
.shell-rib { fill: #7f8791; }
.roof-lip { fill: none; stroke: #c2c8cf; stroke-width: 1.4; }
.lens { fill: none; stroke: #7b838d; stroke-width: 0.9; }
.fin-band { fill: #6c747d; }
.fin { fill: #b4bac1; }
.fin-shadow { fill: #737b84; }
.glow { fill: #ffc978; opacity: 0.4; }
.glow-soft { opacity: 0.22; }
.platform-wall { fill: #2b333c; }
.platform-glass { fill: #0e1a26; opacity: 0.8; }
.park { fill: #2c4331; }
.path { fill: #6b737d; opacity: 0.7; }
.trunk { fill: #4a4038; }
.leaves { fill: #3a5c42; }

@keyframes sleepers { from { transform: translateX(0); } to { transform: translateX(-18px); } }
@keyframes scenery { from { transform: translateX(0); } to { transform: translateX(-800px); } }
@keyframes flash { 0%, 50% { opacity: 1; } 50.01%, 100% { opacity: 0.22; } }
@keyframes sway { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-0.8px); } }
@keyframes lamp { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }

/* Up the ramp, along the deck, and down the far one.

   These percentages are geometry, not taste. The height is the ramp's height
   under the MIDDLE of the train — 21.75% is the moment the middle reaches the
   foot of the ramp, 41.75% the moment it reaches the top, and the two on the
   far side are the same points on the way down. Between them the value is
   interpolated linearly, which is exactly what a constant grade is.

   The lean is one and a half degrees, which is the grade: four units over a
   hundred and sixty. Tilting at the true angle is what keeps the wheels on
   the structure — the rear bogie drops as far below the middle as the ramp
   itself drops over that distance, so it lands on the slope rather than in
   the air above it. It eases in as the cab rides up onto the ramp and out
   again as the tail leaves it. */
@keyframes climb {
  0%, 11.5% { transform: translateY(0) rotate(0deg); }
  21.75% { transform: translateY(0) rotate(-1.5deg); }
  31.5% { transform: translateY(-2px) rotate(-1.5deg); }
  41.75%, 64.25% { transform: translateY(-4px) rotate(0deg); }
  74% { transform: translateY(-2px) rotate(1.5deg); }
  84% { transform: translateY(0) rotate(1.5deg); }
  90%, 100% { transform: translateY(0) rotate(0deg); }
}

/* Brisbane's Merivale bridge: the same ramps at the same moments, half the
   height. The percentages above are positions in the 800-unit pattern, and the
   Brisbane world puts its ramps at the same two places on purpose, so there is
   one set of timings here rather than two sets to keep in step. Two units and
   three quarters of a degree — the approach to the river does rise, and it does
   not rise like a sky-rail viaduct. */
@keyframes rise {
  0%, 11.5% { transform: translateY(0) rotate(0deg); }
  21.75% { transform: translateY(0) rotate(-0.75deg); }
  31.5% { transform: translateY(-1px) rotate(-0.75deg); }
  41.75%, 64.25% { transform: translateY(-2px) rotate(0deg); }
  74% { transform: translateY(-1px) rotate(0.75deg); }
  84% { transform: translateY(0) rotate(0.75deg); }
  90%, 100% { transform: translateY(0) rotate(0deg); }
}

/* A level deck. An empty keyframe rather than `animation: none`, so that every
   world reads its lift from one custom property and there is no branch. */
@keyframes level {
  0%, 100% { transform: translateY(0); }
}

/* The worlds that are drawn from the table. Only the things that are true of
   every one of them: the paint is the table's, exactly as the train's is the
   livery table's. */
.world path { stroke-linecap: round; stroke-linejoin: round; }


@media (prefers-reduced-motion: reduce) {
  .sleepers, .scenery, .scenery-sky, .scenery-mid, .lift, .train, .lamp, .light { animation: none; }
}
</style>
